import React from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
} from '@mui/material';
import styles from './DataTable.module.css';

export interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: 'right' | 'left' | 'center';
  format?: (value: unknown, row?: any) => React.ReactNode;
}

export interface StatusColumn extends Column {
  renderStatus?: boolean;
  statusVariants?: {
    [key: string]: {
      backgroundColor: string;
      textColor: string;
    };
  };
}

export interface DataTableProps<T = Record<string, any>> {
  columns: StatusColumn[];
  data: T[];
  statusField?: keyof T & string;
  rounded?: boolean;
  fullWidth?: boolean;
  maxHeight?: number | string;
  containerClassName?: string;
  headerClassName?: string;
  rowClassName?: string;
  cellClassName?: string;
  loading?: boolean;
  minHeight?: number | string;
  emptyMessage?: string;
  onRowClick?: (row: T) => void;
}

const DataTable = <T extends Record<string, any>>({
  columns,
  data,
  statusField = 'status' as keyof T & string,
  rounded = true,
  fullWidth = true,
  maxHeight,
  containerClassName,
  headerClassName,
  rowClassName,
  cellClassName,
  loading = false,
  minHeight = '200px',
  emptyMessage = 'No data available',
  onRowClick,
}: DataTableProps<T>) => {
  return (
    <Paper elevation={0} className={`${styles.tableContainer} ${containerClassName || ''}`}>
      <TableContainer style={{ maxHeight, minHeight: loading ? minHeight : undefined }}>
        <Table stickyHeader aria-label="data table" className={fullWidth ? styles.fullWidth : ''}>
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell
                  key={column.id}
                  align={column.align || 'left'}
                  style={{
                    minWidth: column.minWidth,
                    height: '16px',
                    borderRight: index < columns.length - 1 ? '1px solid white' : 'none',
                  }}
                  className={`${styles.tableHeadCell} ${column.label === 'Excluir item' ? styles.blackHeaderCell : ''} ${headerClassName || ''} ${rounded && index === 0 ? styles.roundedTopLeft : ''} ${rounded && index === columns.length - 1 ? styles.roundedTopRight : ''}`}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {!loading && (
            <TableBody>
              {data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    <Typography variant="body2" sx={{ py: 2, color: 'text.secondary' }}>
                      {emptyMessage}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row, rowIndex) => {
                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={row.id || rowIndex}
                      className={`${styles.tableRow} ${rowClassName || ''} ${onRowClick ? styles.clickableRow : ''}`}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      sx={onRowClick ? { cursor: 'pointer' } : undefined}
                    >
                      {columns.map((column, colIndex) => {
                        const value = row[column.id];
                        return (
                          <TableCell
                            key={column.id}
                            align={column.align || 'left'}
                            style={{
                              borderRight:
                                colIndex < columns.length - 1 ? '1px solid white' : 'none',
                            }}
                            className={`${styles.tableCell} ${cellClassName || ''}`}
                          >
                            {column.renderStatus && column.id === statusField ? (
                              <StatusBadge
                                status={String(value || '')}
                                variants={column.statusVariants}
                              />
                            ) : column.format ? (
                              column.format(value, row)
                            ) : value !== undefined && value !== null ? (
                              String(value)
                            ) : (
                              ''
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          )}
        </Table>

        {loading && (
          <Box className={styles.spinnerContainer}>
            <Box className={styles.spinner}></Box>
          </Box>
        )}
      </TableContainer>
    </Paper>
  );
};

interface StatusBadgeProps {
  status: string | undefined;
  variants?: {
    [key: string]: {
      backgroundColor: string;
      textColor: string;
    };
  };
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status = '', variants }) => {
  // Default styling if no variants are provided
  let backgroundColor = '#e3f2fd';
  let textColor = '#2196f3';

  // Find the matching variant for this status, if any
  if (variants && variants[status]) {
    backgroundColor = variants[status].backgroundColor;
    textColor = variants[status].textColor;
  } else {
    // Default color mapping if no specific variant is provided
    const statusText = status?.toLowerCase() || '';
    switch (statusText) {
      case 'em negociação':
        backgroundColor = '#e3f2fd';
        textColor = '#2196f3';
        break;
      case 'aprovado':
        backgroundColor = '#fafff6';
        textColor = '#356e01';
        break;
      case 'rejeitado':
        backgroundColor = '#ffdee7';
        textColor = '#b0142e';
        break;
      case 'em análise':
        backgroundColor = '#fffdf0';
        textColor = '#682d03';
        break;
      case 'em transporte':
        backgroundColor = '#ede7f6';
        textColor = '#673ab7';
        break;
      case 'enviado':
        backgroundColor = '#e8f5e9';
        textColor = '#4caf50';
        break;
      case 'processando':
        backgroundColor = '#e3f2fd';
        textColor = '#2196f3';
        break;
    }
  }

  return (
    <span
      className={styles.statusBadge}
      style={{
        backgroundColor,
        color: textColor,
        border: `1px solid ${textColor}`,
        padding: '4px 16px',
      }}
    >
      {status}
    </span>
  );
};

export default DataTable;
